import { ReactNode, useCallback, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { clearUser, setUser } from "@/features/auth/store/auth.slice";
import { AUTH_TOKEN_STORAGE_SYNC_EVENT, tokenService } from "@/services/auth/token.service";
import type { User } from "@/features/auth/model/authTypes";
import { logoutApi } from "@/features/auth/model/authApi";
import { toastService } from "@/shared/toast";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const [hasToken, setHasToken] = useState<boolean>(() => tokenService.hasToken());
  const [isAuthHydrated, setIsAuthHydrated] = useState(false);

  const isAuthenticated = hasToken;

  // useEffect(() => {
  //   // Restore user from localStorage on refresh (no API call).
  //   if (!isAuthenticated || user) return;
  //   const storedUser: User | null = tokenService.getUser();
  //   if (storedUser) dispatch(setUser(storedUser));
  // }, [dispatch, isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && !user) {
      const storedUser: User | null = tokenService.getUser();

      if (storedUser) {
        dispatch(setUser(storedUser));
      }
    }

    setIsAuthHydrated(true);
  }, [dispatch, isAuthenticated, user]);

  useEffect(() => {
    const syncFromPersistence = () => {
      setHasToken(tokenService.hasToken());
    };
    // Same tab: persistAuthToken / OTP / interceptor use tokenService, which emits this event.
    window.addEventListener(AUTH_TOKEN_STORAGE_SYNC_EVENT, syncFromPersistence);
    // Other tabs: native storage event only.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_token") {
        syncFromPersistence();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(AUTH_TOKEN_STORAGE_SYNC_EVENT, syncFromPersistence);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const login = useCallback(
    (nextUser: User) => {
      // Keep context `isAuthenticated` aligned with route guards (`tokenService.hasToken()`).
      dispatch(setUser(nextUser));
      tokenService.setUser(nextUser);
      setHasToken(tokenService.hasToken());
      toastService.show("Signed in successfully", "success");
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    const token = tokenService.getToken();
    const refreshToken = tokenService.getRefreshToken();
    try {
      if (token) {
        await logoutApi(token, refreshToken);
      }
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      tokenService.removeToken();
      tokenService.removeUser();
      setHasToken(false);
      dispatch(clearUser());
      toastService.show("Signed out successfully", "success");
    }
  }, [dispatch]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAuthHydrated,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
